/**
 * CodeMetricsPanel - CESM OSINT System v2.6.0
 * Tableau de bord des métriques de code
 * 
 * Affiche les statistiques en temps réel:
 * - Nombre total de lignes de code
 * - Répartition par répertoire (backend/frontend)
 * - Répartition par type de fichier
 */
import React, { useState, useEffect } from 'react';
import { API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Code, FileText, Folder, TrendingUp, RefreshCw,
  Activity, Terminal, Layers
} from 'lucide-react';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

const COLORS = ['#60a5fa', '#34d399', '#f472b6', '#fbbf24', '#a78bfa', '#f87171'];

const CodeMetricsPanel = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchMetrics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/system/metrics/code`);
      setMetrics(response.data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error fetching code metrics:', error);
      toast.error('Erreur lors du chargement des métriques');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-marine-cyan animate-spin" />
        <span className="ml-3 text-marine-slate">Chargement des métriques...</span>
      </div>
    );
  }

  if (!metrics || metrics.status === 'error') {
    return (
      <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4">
        <p className="text-red-400">Erreur: Impossible de charger les métriques</p>
      </div>
    );
  }

  const totals = metrics.totals || {};
  const byDirectory = metrics.by_directory || {};
  const byExtension = metrics.by_extension || {};

  // Préparer les données pour les graphiques
  const directoryData = Object.entries(byDirectory).map(([name, data]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1),
    value: data.code_lines,
    files: data.files
  }));

  const extensionData = Object.entries(byExtension)
    .map(([ext, data]) => ({
      name: data.name,
      ext: ext,
      lines: data.code_lines,
      files: data.files
    }))
    .sort((a, b) => b.lines - a.lines)
    .slice(0, 6);

  const formatNumber = (num) => {
    return num?.toLocaleString('fr-FR') || '0';
  };

  return (
    <div className="space-y-6" data-testid="code-metrics-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Code className="w-6 h-6 text-marine-cyan" />
          <h2 className="text-xl font-bold text-white">Métriques du Code</h2>
          <span className="text-xs bg-marine-cyan/20 text-marine-cyan px-2 py-1 rounded">v3.0</span>
        </div>
        <button
          onClick={fetchMetrics}
          className="flex items-center gap-2 px-3 py-1.5 bg-marine-slate/30 hover:bg-marine-slate/50 rounded text-sm text-marine-slate hover:text-white transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Actualiser
        </button>
      </div>

      {/* Statistiques principales */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-marine-void/50 border border-marine-slate/30 rounded-lg p-4 text-center">
          <div className="text-3xl font-bold text-marine-cyan">{formatNumber(totals.files)}</div>
          <div className="text-sm text-marine-slate mt-1">Fichiers</div>
          <FileText className="w-5 h-5 text-marine-cyan/50 mx-auto mt-2" />
        </div>
        
        <div className="bg-marine-void/50 border border-marine-slate/30 rounded-lg p-4 text-center">
          <div className="text-3xl font-bold text-green-400">{formatNumber(totals.code_lines)}</div>
          <div className="text-sm text-marine-slate mt-1">Lignes de Code</div>
          <Terminal className="w-5 h-5 text-green-400/50 mx-auto mt-2" />
        </div>
        
        <div className="bg-marine-void/50 border border-marine-slate/30 rounded-lg p-4 text-center">
          <div className="text-3xl font-bold text-purple-400">{formatNumber(totals.total_lines)}</div>
          <div className="text-sm text-marine-slate mt-1">Lignes Totales</div>
          <Layers className="w-5 h-5 text-purple-400/50 mx-auto mt-2" />
        </div>
        
        <div className="bg-marine-void/50 border border-marine-slate/30 rounded-lg p-4 text-center">
          <div className="text-3xl font-bold text-amber-400">{formatNumber(totals.blank_lines)}</div>
          <div className="text-sm text-marine-slate mt-1">Lignes Vides</div>
          <Activity className="w-5 h-5 text-amber-400/50 mx-auto mt-2" />
        </div>
      </div>

      {/* Graphiques */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Répartition par répertoire */}
        <div className="bg-marine-void/50 border border-marine-slate/30 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-marine-slate mb-4 flex items-center gap-2">
            <Folder className="w-4 h-4" />
            Par Répertoire
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={directoryData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={5}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${formatNumber(value)}`}
                >
                  {directoryData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                  formatter={(value, name, props) => [
                    `${formatNumber(value)} lignes (${props.payload.files} fichiers)`,
                    props.payload.name
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Répartition par type de fichier */}
        <div className="bg-marine-void/50 border border-marine-slate/30 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-marine-slate mb-4 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Par Type de Fichier
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={extensionData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                <YAxis 
                  dataKey="name" 
                  type="category" 
                  tick={{ fill: '#94a3b8', fontSize: 12 }} 
                  width={100}
                />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                  formatter={(value, name, props) => [
                    `${formatNumber(value)} lignes (${props.payload.files} fichiers)`,
                    props.payload.ext
                  ]}
                />
                <Bar dataKey="lines" fill="#60a5fa" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Détails par type */}
      <div className="bg-marine-void/50 border border-marine-slate/30 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-marine-slate/30">
          <h3 className="text-sm font-semibold text-marine-slate">Détail par Extension</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-marine-void">
              <tr>
                <th className="px-4 py-2 text-left text-xs text-marine-slate font-medium">Type</th>
                <th className="px-4 py-2 text-left text-xs text-marine-slate font-medium">Extension</th>
                <th className="px-4 py-2 text-right text-xs text-marine-slate font-medium">Fichiers</th>
                <th className="px-4 py-2 text-right text-xs text-marine-slate font-medium">Lignes</th>
                <th className="px-4 py-2 text-right text-xs text-marine-slate font-medium">%</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(byExtension)
                .sort((a, b) => b[1].code_lines - a[1].code_lines)
                .map(([ext, data], idx) => {
                  const percent = ((data.code_lines / totals.code_lines) * 100).toFixed(1);
                  return (
                    <tr key={ext} className="border-t border-marine-slate/20 hover:bg-marine-slate/10">
                      <td className="px-4 py-2 text-sm text-white">{data.name}</td>
                      <td className="px-4 py-2 text-sm text-marine-cyan font-mono">{ext}</td>
                      <td className="px-4 py-2 text-sm text-marine-slate text-right">{data.files}</td>
                      <td className="px-4 py-2 text-sm text-green-400 text-right font-mono">{formatNumber(data.code_lines)}</td>
                      <td className="px-4 py-2 text-sm text-marine-slate text-right">{percent}%</td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer */}
      {lastUpdated && (
        <div className="text-xs text-marine-slate/60 text-center">
          Dernière mise à jour: {lastUpdated.toLocaleTimeString('fr-FR')}
        </div>
      )}
    </div>
  );
};

export default CodeMetricsPanel;
